import React, { useState, useEffect } from 'react';
import { Building2, TrendingUp, Users, Clock, AlertTriangle, CheckCircle, Crown, Loader2, BarChart3, MapPin } from 'lucide-react';
import { API } from '../../config/apiBase';


function MetricBar({ label, values, max, color, unit = '' }) {
    return (
        <div className="mb-3">
            <p className="text-[10px] font-medium mb-1.5" style={{ color: 'var(--muted)' }}>{label}</p>
            <div className="space-y-1.5">
                {values.map((v) => (
                    <div key={v.name} className="flex items-center gap-2">
                        <span className="text-[9px] w-20 truncate shrink-0" style={{ color: 'var(--text)' }}>{v.name}</span>
                        <div className="flex-1 h-5 rounded-md overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                            <div className="h-full rounded-md flex items-center px-2 transition-all duration-500"
                                style={{ width: `${Math.max((v.value / Math.max(max, 1)) * 100, 4)}%`, background: color, minWidth: 24 }}>
                                <span className="text-[9px] font-bold" style={{ color: '#0A0A0A' }}>{v.value}{unit}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function EmpresaComparativoPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEnterprise, setIsEnterprise] = useState(true);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/ponto/unidades/comparativo`, { headers });
                if (r.status === 403) { setIsEnterprise(false); setLoading(false); return; }
                if (r.ok) { const d = await r.json(); setData(d); }
            } catch {}
            setLoading(false);
        })();
    }, []);

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    if (!isEnterprise) {
        return (
            <div className="text-center py-16 space-y-4" data-testid="comparativo-upgrade">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)' }}>
                    <Crown size={28} style={{ color: 'var(--sv-gold-primary)' }} />
                </div>
                <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Comparativo entre Unidades</h3>
                <p className="text-xs max-w-sm mx-auto" style={{ color: 'var(--muted)' }}>
                    Compare métricas de presença, pontualidade e produtividade entre suas filiais. Disponível no plano Enterprise.
                </p>
                <a href="/sistema/empresa/assinatura" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:scale-105"
                    style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="upgrade-enterprise-btn">
                    <Crown size={14} /> Ver plano Enterprise
                </a>
            </div>
        );
    }

    const unidades = data?.unidades || [];
    const periodo = data?.periodo || '';
    const activeUnits = unidades.filter(u => u.total_colaboradores > 0 || u.is_matriz);

    // Find max values for bars
    const maxPresenca = Math.max(...activeUnits.map(u => u.taxa_presenca), 1);
    const maxHoras = Math.max(...activeUnits.map(u => u.horas_semana), 1);
    const maxPontualidade = Math.max(...activeUnits.map(u => u.pontualidade), 1);
    const maxColabs = Math.max(...activeUnits.map(u => u.total_colaboradores), 1);

    // Summary
    const totalColabs = unidades.reduce((a, u) => a + u.total_colaboradores, 0);
    const totalPresentes = unidades.reduce((a, u) => a + u.presentes_hoje, 0);
    const avgPresenca = totalColabs > 0 ? Math.round((totalPresentes / totalColabs) * 100) : 0;
    const totalHoras = unidades.reduce((a, u) => a + u.horas_semana, 0);
    const totalAjustes = unidades.reduce((a, u) => a + u.ajustes_pendentes, 0);

    return (
        <div className="space-y-5" data-testid="empresa-comparativo">
            {/* Header */}
            <div className="rounded-xl p-5" style={{ background: 'linear-gradient(135deg, rgba(var(--sv-accent-rgb), 0.12) 0%, rgba(var(--sv-accent-rgb), 0.02) 100%)', border: '1px solid rgba(var(--sv-accent-rgb), 0.2)' }}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <h2 className="text-lg font-bold flex items-center gap-2" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>
                            <BarChart3 size={20} style={{ color: 'var(--sv-accent)' }} /> Comparativo entre Unidades
                        </h2>
                        <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                            Período: {periodo} — {activeUnits.length} unidade(s) com colaboradores
                        </p>
                    </div>
                    <span className="text-[10px] px-2.5 py-1 rounded-full font-semibold" style={{ background: 'rgba(var(--sv-accent-rgb), 0.15)', color: 'var(--sv-accent)' }}>Enterprise</span>
                </div>
            </div>

            {/* Global KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                    { label: 'Colaboradores', value: totalColabs, icon: Users, color: '#3B82F6' },
                    { label: 'Presença Hoje', value: `${avgPresenca}%`, icon: TrendingUp, color: avgPresenca >= 70 ? '#10B981' : '#F59E0B' },
                    { label: 'Horas (7d)', value: `${totalHoras.toFixed(0)}h`, icon: Clock, color: 'var(--sv-gold-primary)' },
                    { label: 'Ajustes Pendentes', value: totalAjustes, icon: AlertTriangle, color: totalAjustes > 0 ? '#F59E0B' : '#10B981' },
                ].map((c) => (
                    <div key={c.label} className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center gap-1.5 mb-1.5"><c.icon size={12} style={{ color: c.color }} /><span className="text-[10px]" style={{ color: 'var(--muted)' }}>{c.label}</span></div>
                        <p className="text-xl font-bold" style={{ color: 'var(--text)' }}>{c.value}</p>
                    </div>
                ))}
            </div>

            {/* Comparative Bars */}
            {activeUnits.length > 1 && (
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Comparativo Visual</h3>
                    <MetricBar label="Taxa de Presença Hoje" values={activeUnits.map(u => ({ name: u.name, value: u.taxa_presenca }))} max={100} color="#10B981" unit="%" />
                    <MetricBar label="Pontualidade (7 dias)" values={activeUnits.map(u => ({ name: u.name, value: u.pontualidade }))} max={100} color="#3B82F6" unit="%" />
                    <MetricBar label="Horas Trabalhadas (7 dias)" values={activeUnits.map(u => ({ name: u.name, value: u.horas_semana }))} max={maxHoras} color="#7DB3FF" unit="h" />
                    <MetricBar label="Colaboradores" values={activeUnits.map(u => ({ name: u.name, value: u.total_colaboradores }))} max={maxColabs} color="#8B5CF6" />
                </div>
            )}

            {/* Unit Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {activeUnits.map((u) => {
                    const presColor = u.taxa_presenca >= 80 ? '#10B981' : u.taxa_presenca >= 50 ? '#F59E0B' : '#EF4444';
                    const pontColor = u.pontualidade >= 90 ? '#10B981' : u.pontualidade >= 70 ? '#F59E0B' : '#EF4444';
                    return (
                        <div key={u.unidade_id || 'sem'} className="rounded-xl p-4" style={{ background: 'var(--surface)', border: `1px solid ${u.is_matriz ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}` }}
                            data-testid={`comp-unit-${u.unidade_id || 'sem'}`}>
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                                    style={{ background: u.is_matriz ? 'rgba(var(--sv-accent-rgb), 0.12)' : 'rgba(59,130,246,0.12)' }}>
                                    <Building2 size={14} style={{ color: u.is_matriz ? '#2DD4BF' : '#3B82F6' }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{u.name}</p>
                                    <p className="text-[9px]" style={{ color: 'var(--muted)' }}>{u.total_colaboradores} colaborador(es)</p>
                                </div>
                                {u.is_matriz && <span className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)', color: 'var(--sv-accent)' }}>Matriz</span>}
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { label: 'Presença', value: `${u.taxa_presenca}%`, color: presColor },
                                    { label: 'Pontualidade', value: `${u.pontualidade}%`, color: pontColor },
                                    { label: 'Horas (7d)', value: `${u.horas_semana}h`, color: 'var(--sv-gold-primary)' },
                                ].map(m => (
                                    <div key={m.label} className="rounded-lg p-2 text-center" style={{ background: 'var(--bg)' }}>
                                        <p className="text-lg font-bold" style={{ color: m.color }}>{m.value}</p>
                                        <p className="text-[8px]" style={{ color: 'var(--muted)' }}>{m.label}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="grid grid-cols-4 gap-1.5 mt-2">
                                {[
                                    { label: 'Presentes', value: u.presentes_hoje, color: '#10B981' },
                                    { label: 'Ausentes', value: u.ausentes_hoje, color: '#EF4444' },
                                    { label: 'Atrasos', value: u.atrasos_semana, color: '#F59E0B' },
                                    { label: 'Ajustes', value: u.ajustes_pendentes, color: '#8B5CF6' },
                                ].map(m => (
                                    <div key={m.label} className="text-center py-1.5">
                                        <p className="text-sm font-bold" style={{ color: m.color }}>{m.value}</p>
                                        <p className="text-[7px]" style={{ color: 'var(--muted)' }}>{m.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {activeUnits.length <= 1 && (
                <div className="rounded-xl p-5 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <Building2 size={24} style={{ color: 'var(--muted)', opacity: 0.5 }} className="mx-auto mb-3" />
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Cadastre mais unidades para comparar</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                        Vincule colaboradores a diferentes unidades para ver o comparativo lado a lado.
                    </p>
                    <a href="/sistema/empresa/unidades" className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold" style={{ color: 'var(--sv-accent)' }}>
                        Gerenciar Unidades →
                    </a>
                </div>
            )}

            {/* Tip */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.04)', border: '1px solid rgba(var(--sv-accent-rgb), 0.1)' }}>
                <p className="text-xs font-semibold" style={{ color: 'var(--sv-gold-primary)' }}>Dica Enterprise</p>
                <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted)' }}>
                    Vincule cada colaborador a uma unidade na página de Colaboradores para métricas mais precisas. Os dados são atualizados em tempo real.
                </p>
            </div>
        </div>
    );
}
